/**
 * Data Prep Engine Types
 * 
 * Provides types for the data cleaning and normalization pipeline.
 * Steps are applied sequentially to transform raw dataset data.
 */

// ============================================================================
// Column Schema & Stats
// ============================================================================

export interface ColumnStats {
    nullCount: number;
    uniqueCount: number;
    min?: number | string | Date;
    max?: number | string | Date;
    mean?: number;
    median?: number;
    mode?: string | number;
}

export interface ColumnSchema {
    name: string;
    type: 'string' | 'number' | 'date' | 'boolean';
    stats: ColumnStats;
    warnings: string[];
    hidden?: boolean;
}

// ============================================================================
// Data Prep Step Types
// ============================================================================

export type NullReplacement = '0' | 'mean' | 'median' | 'mode' | 'custom';

export interface NullHandlingStep {
    type: 'null-handling';
    column: string;
    action: 'remove-rows' | 'replace';
    replacement?: NullReplacement;
    customValue?: string | number;
}

export interface DuplicateHandlingStep {
    type: 'duplicate-handling';
    keyColumns: string[]; // Empty means all columns
}

export interface StringCleaningStep {
    type: 'string-cleaning';
    column: string;
    operations: ('trim' | 'lowercase' | 'uppercase')[];
    findReplace?: { find: string; replace: string };
}

export interface DataTypeCastingStep {
    type: 'type-casting';
    column: string;
    targetType: 'string' | 'number' | 'date';
    removeInvalid: boolean;
}

export type NormalizationMethod = 'min-max' | 'z-score' | 'log' | 'abs' | 'pct-change';

export interface NormalizationStep {
    type: 'normalization';
    columns: string[];
    method: NormalizationMethod;
    createNewColumn: boolean;
    newColumnSuffix?: string; // e.g., "_normalized"
}

export interface CalculatedColumnStep {
    type: 'calculated-column';
    newColumnName: string;
    formula: string; // Safe expression, e.g., "revenue / users"
}

export type ColumnManagementAction = 'rename' | 'hide' | 'delete' | 'reorder';

export interface ColumnManagementStep {
    type: 'column-management';
    action: ColumnManagementAction;
    column: string;
    newName?: string;        // For rename
    hidden?: boolean;        // For hide
    newPosition?: number;    // For reorder
}

export type DataPrepStep =
    | NullHandlingStep
    | DuplicateHandlingStep
    | StringCleaningStep
    | DataTypeCastingStep
    | NormalizationStep
    | CalculatedColumnStep
    | ColumnManagementStep;

export type DataPrepStepType = DataPrepStep['type'];

// ============================================================================
// Step Metadata & Results
// ============================================================================

export interface DataPrepStepWithMeta {
    id: string;
    step: DataPrepStep;
    enabled: boolean;
    order: number;
}

export interface DataPrepWarning {
    stepId: string;
    column?: string;
    message: string;
    severity: 'info' | 'warning' | 'error';
    affectedRows?: number;
}

export interface StepResult {
    data: Record<string, any>[];
    schema: ColumnSchema[];
    warnings: DataPrepWarning[];
    stats: {
        inputRows: number;
        outputRows: number;
        rowsRemoved: number;
        columnsAdded: number;
        columnsModified: number;
        executionTimeMs: number;
    };
}

// ============================================================================
// Pipeline Configuration
// ============================================================================

export interface DataPrepConfig {
    previewRowLimit: number;      // Soft limit for preview (default 5000)
    enableCaching: boolean;       // Enable memoization
    stopOnError: boolean;         // Stop pipeline on first error?
}

export const DEFAULT_DATA_PREP_CONFIG: DataPrepConfig = {
    previewRowLimit: 5000,
    enableCaching: true,
    stopOnError: false
};

// ============================================================================
// Pipeline Result
// ============================================================================

export interface DataPrepPipelineResult {
    data: Record<string, any>[];
    schema: ColumnSchema[];
    warnings: DataPrepWarning[];
    stepResults: Map<string, StepResult>;  // stepId -> result
    stats: {
        totalInputRows: number;
        totalOutputRows: number;
        totalExecutionTimeMs: number;
        isPreviewMode: boolean;
        wasCached: boolean;
    };
}

// ============================================================================
// Expression Parser Types (for Calculated Columns)
// ============================================================================

export type ExpressionOperator = '+' | '-' | '*' | '/' | '%' | '>' | '<' | '>=' | '<=' | '==' | '!=' | '&&' | '||';

export type ExpressionFunction = 'abs' | 'log' | 'log10' | 'round' | 'floor' | 'ceil' | 'sqrt' | 'pow' | 'min' | 'max' | 'if';

export const ALLOWED_FUNCTIONS: ExpressionFunction[] = [
    'abs', 'log', 'log10', 'round', 'floor', 'ceil', 'sqrt', 'pow', 'min', 'max', 'if'
];

export const ALLOWED_OPERATORS: ExpressionOperator[] = [
    '+', '-', '*', '/', '%', '>', '<', '>=', '<=', '==', '!=', '&&', '||'
];

export interface ParsedExpression {
    type: 'literal' | 'column' | 'operator' | 'function' | 'binary' | 'conditional';
    value?: number | string;
    columnName?: string;
    operator?: ExpressionOperator;
    functionName?: ExpressionFunction;
    left?: ParsedExpression;
    right?: ParsedExpression;
    args?: ParsedExpression[];
    condition?: ParsedExpression;
    trueBranch?: ParsedExpression;
    falseBranch?: ParsedExpression;
}

// ============================================================================
// Helper type for step display in UI
// ============================================================================

export interface DataPrepStepDisplay {
    type: DataPrepStepType;
    label: string;
    icon: string;
    description: string;
}

export const DATA_PREP_STEP_DISPLAYS: Record<DataPrepStepType, DataPrepStepDisplay> = {
    'null-handling': {
        type: 'null-handling',
        label: 'Null Handling',
        icon: 'AlertCircle',
        description: 'Handle missing or null values'
    },
    'duplicate-handling': {
        type: 'duplicate-handling',
        label: 'Remove Duplicates',
        icon: 'Copy',
        description: 'Remove duplicate rows'
    },
    'string-cleaning': {
        type: 'string-cleaning',
        label: 'String Cleaning',
        icon: 'Type',
        description: 'Clean and transform text values'
    },
    'type-casting': {
        type: 'type-casting',
        label: 'Type Casting',
        icon: 'RefreshCw',
        description: 'Convert column data types'
    },
    'normalization': {
        type: 'normalization',
        label: 'Normalization',
        icon: 'TrendingUp',
        description: 'Scale and normalize numeric data'
    },
    'calculated-column': {
        type: 'calculated-column',
        label: 'Calculated Column',
        icon: 'Calculator',
        description: 'Create new columns from formulas'
    },
    'column-management': {
        type: 'column-management',
        label: 'Column Management',
        icon: 'Columns',
        description: 'Rename, hide, or delete columns'
    }
};
